import { useState } from "react";
import { Link } from "react-router-dom";

const VOLUNTEER_ROLES = [
  {
    id: "checkin",
    title: "Check-In Team",
    icon: "📋",
    desc: "Welcome attendees, scan tickets, and register walk-ins at events.",
    commitment: "Event days",
    skills: "Friendly, organized, tech-comfortable",
  },
  {
    id: "pos",
    title: "AekamMart Volunteer",
    icon: "🛒",
    desc: "Manage sacred items booth — organize inventory, process sales, answer questions.",
    commitment: "Event days",
    skills: "Sales, customer service",
  },
  {
    id: "av",
    title: "A/V & Streaming",
    icon: "🎥",
    desc: "Handle audio, video, and live-stream setup for satsangs and retreats.",
    commitment: "Flexible",
    skills: "Technical, audio/video",
  },
  {
    id: "content",
    title: "Content & Social Media",
    icon: "✍️",
    desc: "Create and share wisdom content, event recaps, and community stories.",
    commitment: "Remote / ongoing",
    skills: "Writing, social media, design",
  },
  {
    id: "translation",
    title: "Translation & Outreach",
    icon: "🌍",
    desc: "Help translate teachings into regional languages and connect communities.",
    commitment: "Remote / flexible",
    skills: "Multilingual, cultural awareness",
  },
  {
    id: "sena",
    title: "Sena (General Service)",
    icon: "🌿",
    desc: "General seva at events — setup, cleanup, logistics, and hospitality.",
    commitment: "Event days",
    skills: "Team player, willing to help",
  },
];

export function VolunteerPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);

  function toggleRole(id: string) {
    setSelectedRoles((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
  }

  if (submitted) {
    return (
      <div className="container" style={{ padding: "5rem 0", textAlign: "center" }}>
        <div className="card" style={{ maxWidth: "520px", margin: "0 auto", padding: "3rem 2rem" }}>
          <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🌿</div>
          <h1 className="display-3" style={{ marginBottom: "0.75rem" }}>Application Received!</h1>
          <p className="body-lg text-muted">
            Thank you for offering your service. Our team will reach out to you at{" "}
            <strong>{email}</strong> with next steps. We are grateful for your seva.
          </p>
          <div style={{ marginTop: "2rem", display: "flex", gap: "1rem", justifyContent: "center" }}>
            <Link to="/" className="btn btn--brand">Return Home</Link>
            <Link to="/events" className="btn btn--outline">See Upcoming Events</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Hero */}
      <section className="page-hero page-hero--brand">
        <div className="container container--narrow" style={{ textAlign: "center" }}>
          <span className="eyebrow">Seva — Selfless Service</span>
          <h1 className="display-1 page-hero__title-spaced">Volunteer with Us</h1>
          <p className="body-xl text-muted">
            Join our Sena — a community of dedicated volunteers who give their time
            and skills to spread the teachings and support seekers on the path.
          </p>
        </div>
      </section>

      {/* Roles */}
      <section className="section section--surface-alt">
        <div className="container">
          <div className="section-header">
            <span className="eyebrow">Volunteer Opportunities</span>
            <h2 className="display-3">Where You Can Contribute</h2>
          </div>
          <div className="volunteer-roles-grid">
            {VOLUNTEER_ROLES.map((role) => (
              <div key={role.id} className="volunteer-role-card">
                <div className="volunteer-role-card__icon">{role.icon}</div>
                <h3 className="volunteer-role-card__title">{role.title}</h3>
                <p className="volunteer-role-card__desc">{role.desc}</p>
                <div className="volunteer-role-card__meta">
                  <span className="volunteer-role-card__meta-item">
                    🕐 {role.commitment}
                  </span>
                  <span className="volunteer-role-card__meta-item">
                    ✅ {role.skills}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application form */}
      <section className="section">
        <div className="container">
          <div className="volunteer-form-wrapper">
            <div className="section-header" style={{ marginBottom: "2rem" }}>
              <span className="eyebrow">Join the Sena</span>
              <h2 className="display-3">Volunteer Application</h2>
            </div>
            <form className="volunteer-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="v-name">Full Name *</label>
                  <input
                    id="v-name"
                    type="text"
                    className="form-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your full name"
                    required
                    autoComplete="name"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="v-email">Email *</label>
                  <input
                    id="v-email"
                    type="email"
                    className="form-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="v-phone">Phone (optional)</label>
                <input
                  id="v-phone"
                  type="tel"
                  className="form-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  autoComplete="tel"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Roles I'm interested in *</label>
                <div className="volunteer-role-checkboxes">
                  {VOLUNTEER_ROLES.map((role) => (
                    <label key={role.id} className="volunteer-checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedRoles.includes(role.id)}
                        onChange={() => toggleRole(role.id)}
                        className="volunteer-checkbox"
                      />
                      <span>
                        {role.icon} {role.title}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="v-message">Why do you want to volunteer?</label>
                <textarea
                  id="v-message"
                  className="form-input"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Share a bit about yourself and your motivation…"
                  rows={4}
                  style={{ resize: "vertical" }}
                />
              </div>
              <button
                type="submit"
                className="btn btn--brand"
                style={{ marginTop: "0.5rem" }}
                disabled={!name.trim() || !email.trim() || selectedRoles.length === 0}
              >
                Submit Application 🌿
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
